import useCustomerCountry from 'hooks/shared/useCustomerCountry';
import useExpensesCategories from 'hooks/useExpensesCategories';
import { useCallback, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { CurrenciesEnum } from 'types/global.types';
import formatMoney from 'utils/formatMoney';
import yearlyReviewExpensesCategories from '../../categories';

const getCategorySubtitle = (percentile: number) => {
  if (!percentile) return '';

  if (percentile >= 95) {
    return 'yearly_review.deductible_expenses_widget.category_distribution.top_5';
  }

  if (percentile >= 65) {
    return 'yearly_review.deductible_expenses_widget.category_distribution.more_than_65';
  }

  if (percentile <= 35) {
    return 'yearly_review.deductible_expenses_widget.category_distribution.less_than_35';
  }

  return 'yearly_review.deductible_expenses_widget.category_distribution.similar_to_others';
};

const TopDeductibleExpensesCategory: FC<{
  categoryId: string;
  amount: number;
  percentile: number;
  language: string;
}> = ({ categoryId, amount, percentile, language }) => {
  const { t } = useTranslation();

  const expenseCategories = useExpensesCategories();

  const translateCategoryId = useCallback(
    (id: string) => {
      const cate = expenseCategories.data?.dict?.[id];
      const displayName = cate?.displayName;
      const title = cate?.title;
      return displayName || title || id;
    },
    [expenseCategories.data?.dict],
  );

  const country = useCustomerCountry();
  const categories = yearlyReviewExpensesCategories[country!];
  const category = categories[categoryId as keyof typeof categories];

  const langKey = language.toUpperCase() as keyof typeof category;

  const translatedCategory =
    category?.[langKey] || translateCategoryId(categoryId);

  return (
    <div className="flex items-center justify-between border-b border-primary-50 py-3">
      <div className="w-[75%]">
        <p className="font-semibold text-md text-primary">
          {translatedCategory}
        </p>

        {percentile ? (
          <p className="text-primary text-sm">
            {t(getCategorySubtitle(percentile), { percentile })}
          </p>
        ) : null}
      </div>

      <div className="font-semibold text-lg text-primary-900 w-[25%] text-right">
        {formatMoney(amount, CurrenciesEnum.EUR, undefined, false, true)}
      </div>
    </div>
  );
};

export default TopDeductibleExpensesCategory;
